// src/pages/secondLife/fehler.js
//
// Serverantworten in EINEN Satz verwandeln — die EINE Stelle, an der ein
// Fehlercode der Second-Life-Routen **auf der Bestandsseite** zu Text wird.
//
// 🔴 **„der Second-Life-Routen" stand hier bis zum 2026-10-09 zu weit**
// (Regel-15-W5 der Runde 5). `SecondLifeQueue.jsx:97` fuehrte daneben ein
// EIGENES `AKTIONS_FEHLER` mit ABWEICHENDEN Wortlauten fuer sechs derselben
// Codes (`a_produkt_unlesbar`, `shopify_lesefehler`, `kein_text`,
// `entwurf_unlesbar`, `kein_kanal`, `kanal_lesefehler`). Dieselbe Antwort las
// sich auf den zwei Seiten also verschieden.
//
// 🟢 **Seit dem 2026-10-10 gegenstandslos: die Warteschlange ist stillgelegt.**
// `/second-life` IST die Bestandsansicht, `SecondLifeQueue.jsx` ist geloescht —
// es gibt nur noch EINE Abbildung, und der Satz „die EINE Stelle" gilt jetzt
// ohne Einschraenkung. Die Backlog-Karte zur Zusammenfuehrung
// (`docs/backlog-auto-repair/2026-10-09-second-life-zwei-fehlerabbildungen.md`)
// ist damit erledigt.
//
// 🔴 **Warum ein eigenes Modul und nicht ein Block in `SecondLifeBestand.jsx`**
// (Grill W5 / Regel-16-W2 der Runde 4): die Seite traegt ueber 900 Zeilen, und
// eine Funktion darin ist ohne den ganzen Baum nicht pruefbar. Die Hausform
// steht in `rma/inbox/fehlerklartext.js` — „Why a module and not a block … the
// mapping to live in ONE place". Hier gilt dasselbe.
//
// ── Die zwei Dinge, die NIE in der Oberflaeche stehen ────────────────────────
//
// 1. **Ein roher Ausnahmetext.** `POST /<id>/anlegen` antwortet bei einem
//    Lesefehler mit `{"error": "shopify_lesefehler", "detail": str(exc)[:300]}`
//    (`backend/second_life.py:2061-2062`); `entwurf_unlesbar`, `freigabe_fehler`,
//    `shopify_fehler` und `veroeffentlichen_fehler` tragen dieselbe Form. Ein
//    solcher `detail`-String ist eine Aussage ueber den Code, nicht fuer den
//    Menschen — er darf nie als Satz erscheinen.
//
// 2. **Ein roher Code.** `POST /<id>/anlegen` reicht das Innere von
//    `lege_listing_an` durch: `{"error": "<code>", "detail": {"angelegt": false,
//    "grund": "<code>"}}` (`:2135`). Ohne Abbildung laese der Mensch dort
//    `keine_aggregat_zeile_unit` — mit Unterstrich.
//
// 🔴 **Die Hausform des Schwesterbauteils derselben Woche:** `RabattBlock.jsx:52-54`
// — *„Ein Code, der hier fehlt, erscheint NICHT roh in der Oberflaeche — der
// Rueckfall ist ein allgemeiner Satz."* `serverSatz` ist genau dieser Rueckfall.
//
// ⚠️ **Der Vertrag ist NICHT „jeder Code hat einen Satz".** Er ist: „kein Code
// und kein Ausnahmetext erreicht den Menschen." Ein unbekannter Code faellt auf
// den uebergebenen allgemeinen Satz — nicht auf `d.detail` und nicht auf
// `d.detail.grund`.

/**
 * Die Codes, die die Seite sieht, und ihr Satz. Der Schluessel ist genau
 * `response.data.error`.
 *
 * 🔴 Jeder Satz nennt die FOLGE, nicht die Mechanik („es wurde nichts
 * geschrieben", nicht „die Transaktion wurde zurueckgerollt"). Bei den
 * Shopify-Fehlern ist die Folge der wichtige Teil: der Mensch soll wissen, ob er
 * erneut klicken darf.
 */
export const AKTIONS_FEHLER = {
  // ── Ein Schalter steht aus. Kein Defekt, eine Antwort. ────────────────────
  anlage_aus: 'Creation is not enabled yet. The switch is off.',
  beschreibung_aus:
    'Editing the description is not enabled yet. The switch is off.',
  veroeffentlichen_aus: 'Publishing is not enabled yet. The switch is off.',

  // ── Die Zeile selbst traegt den Grund. ────────────────────────────────────
  zeile_fehlt: 'This case has no ledger row yet — a graded return creates one.',
  kein_verkauf: 'DEF goods are not sold — their route is the vendor credit note.',
  ausgesondert: 'Scrap — this item is not sold.',
  zustand: 'The row is in a state in which this step is not possible.',

  // ── Der Weg des Anlegens (`lege_listing_an`). ─────────────────────────────
  kein_rabatt:
    'Without a confirmed discount no draft is created. Please confirm the discount first.',
  kein_grundprodukt:
    'Without a base product nothing can be created — the A price and the cost price are missing.',
  kein_textvorschlag:
    'There is no text suggestion for the draft yet. Please try again later.',
  kein_text: 'The draft text is empty. Please enter a text.',
  a_vk_fehlt: 'The A sales price is missing — without it no discount price can be computed.',
  rabatt_ausserhalb: 'The discount is outside the permitted range.',
  // 🔴 **Der Datenwiderspruch der Anlage** (Regel-15-Befund W-N2, 2026-10-30;
  // `backend/second_life.py` → `AnlageDatenwiderspruch` → **409
  // `datenwiderspruch`**). Ohne diesen Eintrag fiel der Code auf
  // `ALLGEMEINER_SATZ` zurueck — „The action did not go through. Please try
  // again." — und der Satz raet zum zweiten Klick, obwohl genau dieser Fall
  // der eine ist, in dem ein zweiter Klick NICHT hilft: POG haelt zwei
  // `shp_products`-Zeilen zu derselben SKU, und erst die Dublette muss weg.
  //
  // ⚠️ **Heute unerreichbar** (W-N5): der globale UNIQUE-Index
  // `shp_products_sku_key` laesst zwei Zeilen je SKU nicht zu. Der Eintrag ist
  // trotzdem richtig — die Route kann den Code senden, sobald die
  // Eindeutigkeit faellt, und dann soll der Mensch den richtigen Satz lesen.
  datenwiderspruch:
    'POG holds more than one product row for this SKU. Retrying will not '
    + 'help — the duplicate row must be removed first.',

  // ── Der Weg der Freigabe (`approve`). ─────────────────────────────────────
  kein_bestand: 'Without sellable stock nothing is released.',
  a_preis_geaendert:
    'The A price has changed since creation. Please confirm again.',
  description_entfernt:
    'The text does not belong to the release. Please save the wording via “Edit”.',

  // ── Shopify und die Lesungen — es wurde NICHTS geaendert. ─────────────────
  a_produkt_unlesbar:
    'The description of the base product cannot be read — nothing was written.',
  entwurf_unlesbar: 'The draft cannot be read — nothing was changed.',
  achsen_unlesbar: 'The options of the product cannot be read — nothing was changed.',
  kanal_lesefehler: 'The sales channels of the product cannot be read.',
  kein_kanal: 'The product has no sales channel.',
  shopify_ohne_antwort: 'Shopify did not answer. Please try again.',
  shopify_lesefehler:
    'The Shopify call failed — nothing was created. Please try again.',
  // 🔴 **Der SCHREIBweg der Anlage — ein eigener Code** (Auftrag 2026-10-14).
  // Bis dahin fiel jede Ausnahme aus `lege_listing_an` in `shopify_lesefehler`,
  // auch die aus dem Schreiben (`productSet`). Der Mensch las „Lesefehler",
  // wenn Shopify die Anlage **abgelehnt** hatte — und erfuhr die Ursache nie,
  // weil `serverSatz` `detail` absichtlich nicht liest.
  //
  // ⚠️ **Der Satz sagt NICHT „nothing was created".** Das ist der Unterschied
  // zum Lesefehler: bei einer Ablehnung wurde nichts angelegt, bei einer
  // Zeitueberschreitung **weiss es niemand**. Eine Zusage, die nur in einem der
  // beiden Faelle stimmt, ist die Sorte Satz, gegen die diese Datei gebaut ist
  // — deshalb der Auftrag, im Zweifel in Shopify nachzusehen.
  shopify_schreibfehler:
    'Shopify did not accept the write — please check the draft in Shopify '
    + 'before trying again.',
  shopify_fehler:
    'Shopify did not accept the change — nothing was saved.',
  freigabe_fehler:
    'The release failed. Please try again — a second click heals the half state.',
  veroeffentlichen_fehler:
    'Publishing in the shop failed. Please try again.',

  // Der `errorhandler` der Blaupause (Grill W6) — ohne ihn stuende hier Flasks
  // HTML-Fehlerseite, und der Grund waere nirgends.
  interner_fehler:
    'Unexpected error in the Second-Life interface. Please try again later.',
};

/**
 * Codes, die einen WERT im Namen tragen — der Server bildet sie als
 * `f"…_{wert}"`. Erkannt wird die FAMILIE, damit kein Unterstrich-Code stehen
 * bleibt.
 *
 *   `keine_aggregat_zeile_unit`   ← `second_life.py:955`
 *   `zustand_<state>`             ← `:943`
 *   `unbekannte_stufe_<X>`        ← `:991`
 *
 * Die Reihenfolge zaehlt nicht: die Praefixe schliessen einander aus.
 */
const FEHLER_PRAEFIX = [
  [
    'keine_aggregat_zeile',
    'This row is not an aggregate row — it is merged automatically '
      + 'and cannot be created here.',
  ],
  ['zustand_', 'The row is in a state in which this step is not possible.'],
  [
    'unbekannte_stufe',
    'The level of the row is unknown — the step is locked.',
  ],
];

/** Der allgemeine Satz, wenn weder `ersatz` noch ein Code etwas hergibt. */
export const ALLGEMEINER_SATZ =
  'The action did not go through. Please try again.';

/**
 * Der Satz zu einem Code — oder `null`, wenn keiner bekannt ist.
 *
 * `typeof … === 'string'` und nicht `in`: `constructor` und Verwandte sind
 * geerbte Eigenschaften und wuerden sonst als Satz zurueckkommen (dieselbe
 * Wache wie in `rma/inbox/fehlerklartext.js:97-100`).
 */
export function fehlersatz(code) {
  if (typeof code !== 'string' || !code) return null;
  if (typeof AKTIONS_FEHLER[code] === 'string') return AKTIONS_FEHLER[code];
  const treffer = FEHLER_PRAEFIX.find(([praefix]) => code.startsWith(praefix));
  return treffer ? treffer[1] : null;
}

/**
 * Eine Serverantwort in EINEN Satz verwandeln — nie in ein Objekt, nie in einen
 * Code, nie in einen rohen Ausnahmetext.
 *
 * 🔴 **Das ist der Kern von Blocker B3.** `/anlegen` antwortet mit
 * `{"error": grund, "detail": ergebnis}`, und `ergebnis` ist ein OBJEKT
 * (`{"angelegt": false, "grund": "kein_rabatt"}`). Gibt man `detail` roh
 * zurueck, landet es in `<Alert>{…}</Alert>`, React wirft „Objects are not
 * valid as a React child", und die ErrorBoundary zeigt die Fehlerseite statt
 * des Falls.
 *
 * 🔴 **Und seit Runde 4 gibt es einen zweiten Grund, `detail` gar nicht erst zu
 * lesen:** es traegt bei den Lesefehlern `str(exc)[:300]` (ein roher
 * Ausnahmetext), bei den Anlege-Fehlern den CODE selbst. Beide sind keine
 * Saetze. Gelesen wird deshalb NUR `error`, und zwar ueber `fehlersatz`.
 *
 * @param {*} err      der Fehler aus dem `catch` (axios-Form erwartet)
 * @param {string} [ersatz] der allgemeine Satz fuer den Aufrufer
 * @returns {string}   immer ein nicht-leerer Satz
 */
export function serverSatz(err, ersatz = ALLGEMEINER_SATZ) {
  const fallback = typeof ersatz === 'string' && ersatz ? ersatz : ALLGEMEINER_SATZ;
  const d = err?.response?.data;
  // Ein String-Rumpf ist eine HTML-Fehlerseite oder ein Proxy-Text — nie ein
  // Satz fuer den Menschen. Auch er faellt auf den allgemeinen Satz.
  if (!d || typeof d !== 'object') return fallback;
  return fehlersatz(d.error) || fallback;
}
